import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	User,
	Lock,
	ShieldCheck,
} from "lucide-react";
import { DashboardLayout } from "@/app/layout/DashboardLayout";
import useGcpsUIController from "@/hooks/useGlobalContext";
import { useForm } from "react-hook-form";
import { profileSchema } from "./profile.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useMutation } from "@tanstack/react-query";
import { updatePassword, updateProfile } from "./account.api";
import { toaster } from "@/components/Toaster";
import { Separator } from "@/components/ui/separator";
import { securitySchema } from "./security.schema";
import { useEffect } from "react";
import { handleMutationError } from "@/lib/utils";

export const AccountPage = () => {
	const { setPageTitle, controller } = useGcpsUIController();
	const { resident } = controller;

	const form = useForm({
		resolver: zodResolver(profileSchema),
		shouldUnregister: false,
		defaultValues: {
			id_number: resident?.resident_number || "",
			first_name: resident?.first_name || "",
			middle_name: "",
			surname: resident?.last_name || "",
			email: resident?.email || "",
			college_email: resident?.college_email || "",
			resident_status: resident?.resident_status || "",
			training_center: resident?.training_center || "",
			department: resident?.department || "",
		},
	});

	const passwordForm = useForm({
		resolver: zodResolver(securitySchema),
		shouldUnregister: false,
		defaultValues: {
			current_password: "",
			password: "",
			password_confirmation: "",
		}
	});

	const mutation = useMutation({
		mutationFn: updateProfile,
		onSuccess: () => {
			toaster('success', 'Profile updated successfully');
		},
		onError: handleMutationError
	});

	const updatePasswordMutation = useMutation({
		mutationFn: updatePassword,
		onSuccess: () => {
			toaster('success', 'Password changed successfully');
			passwordForm.reset()
		},
		onError: handleMutationError
	});

	useEffect(() => {
		setPageTitle("Account Settings")

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div className="mb-8">
					<h1 className="text-3xl font-semibold">Account Settings</h1>
					<p className="text-muted-foreground mt-1">
						Manage your profile, security, and account preferences
					</p>
				</div>
				<div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
					<div className="lg:col-span-1">
						<Card className="sticky top-8 h-auto">
							<CardContent className="pt-6 text-center">
								<Avatar className="h-24 w-24 mx-auto mb-4">
									<AvatarImage src="/avatar.png" />
									<AvatarFallback>MM</AvatarFallback>
								</Avatar>

								<h2 className="font-semibold text-lg">{resident?.first_name} {resident?.last_name}</h2>
								<p className="text-sm text-muted-foreground">
									{resident?.resident_status} Resident
								</p>
								<Separator className="my-1 mt-3" />
								<p className="text-sm text-muted-foreground">
									Faculty of {resident?.faculty}
								</p>
								<Separator className="my-1" />
								<p className="text-sm text-muted-foreground">
									{resident?.subspecialty}
								</p>
								<div className="mt-4 inline-flex items-center gap-1 text-sm text-green-600">
									<ShieldCheck className="h-4 w-4" />
									Verified Account
								</div>

								<Button variant="outline" className="mt-6 w-full">
									Change Photo
								</Button>
							</CardContent>
						</Card>
					</div>

					<div className="lg:col-span-3 space-y-8">
						<Card className="h-auto">
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<User className="h-5 w-5" />
									Personal Information
								</CardTitle>
								<CardDescription>
									Update your personal and contact details
								</CardDescription>
							</CardHeader>

							<CardContent className="">
								<Form {...form}>
									<form onSubmit={form.handleSubmit(() => toaster('warning', "Functionality has been disabled"))} className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<FormField
											control={form.control}
											name="id_number"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input placeholder="01XXXX" {...field} label="ID Number" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="first_name"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="First Name" placeholder="John" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="middle_name"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Middle Name" placeholder="Optional" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="surname"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Surname" placeholder="Doe" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="email" {...field} label="Email" placeholder="john.doe@email.com" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="college_email"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="email" {...field} label="College Email" placeholder="john.doe@gcps.edu.gh" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="training_center"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Training Center" placeholder="Korle Bu Teching Hospital" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="department"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Department" placeholder="Korle Bu Teching Hospital" className="uppercase" readOnly />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
										<div className="md:col-span-2 flex justify-end">
											<Button className="gap-2" type="submit" disabled loading={mutation.isPending}>
												Save Changes
											</Button>
										</div>
									</form>
								</Form>
							</CardContent>
						</Card>

						<Card className="h-auto">
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Lock className="h-5 w-5" />
									Security
								</CardTitle>
								<CardDescription>
									Change your password and secure your account
								</CardDescription>
							</CardHeader>

							<CardContent className="">
								<Form {...passwordForm}>
									<form onSubmit={passwordForm.handleSubmit((values) => updatePasswordMutation.mutate(values))} className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<div className="space-y-2 md:col-span-2">
											<FormField
												control={passwordForm.control}
												name="current_password"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input type="password" {...field} label="Current Password" />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</div>

										<FormField
											control={passwordForm.control}
											name="password"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="password" {...field} label="New Password" />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={passwordForm.control}
											name="password_confirmation"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="password" {...field} label="Confirm New Password" />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<div className="md:col-span-2 flex justify-end">
											<Button type="submit" disabled={updatePasswordMutation.isPending} loading={updatePasswordMutation.isPending}>
												Update Password
											</Button>
										</div>
									</form>
								</Form>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
}
